/* eslint-disable @typescript-eslint/no-explicit-any */
import { supabase } from '../lib/supabaseClient';

/* ── Date-range presets (Turkey timezone aware) ── */

export type DateRangePreset =
  | 'today'
  | 'yesterday'
  | 'this_week'
  | 'this_month'
  | 'this_year'
  | 'last_30_days';

/** Current YYYY-MM-DD in Europe/Istanbul */
function turkeyToday(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** Convert a YYYY-MM-DD (Turkey local) to UTC ISO string at midnight */
function turkeyMidnightUTC(dateStr: string): string {
  // Turkey is permanently UTC+3
  return new Date(dateStr + 'T00:00:00+03:00').toISOString();
}

function pad2(n: number) {
  return String(n).padStart(2, '0');
}

function dateStr(d: Date): string {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function getDateRange(preset: DateRangePreset): { start: string; end: string } {
  const today = turkeyToday();
  const [y, m, d] = today.split('-').map(Number);
  // Use a plain Date for arithmetic only (month/day offsets)
  const td = new Date(y, m - 1, d);

  let startStr: string;

  let endStr: string | null = null; // null = now

  switch (preset) {
    case 'today':
      startStr = today;
      break;
    case 'yesterday': {
      const yd = new Date(td);
      yd.setDate(yd.getDate() - 1);
      startStr = dateStr(yd);
      endStr = today; // end at midnight today (so only yesterday)
      break;
    }
    case 'this_week': {
      const dow = td.getDay(); // 0=Sun
      const toMon = dow === 0 ? 6 : dow - 1;
      const mon = new Date(td);
      mon.setDate(mon.getDate() - toMon);
      startStr = dateStr(mon);
      break;
    }
    case 'this_month':
      startStr = `${y}-${pad2(m)}-01`;
      break;
    case 'this_year':
      startStr = `${y}-01-01`;
      break;
    case 'last_30_days': {
      const s = new Date(td);
      s.setDate(s.getDate() - 29);
      startStr = dateStr(s);
      break;
    }
  }

  return {
    start: turkeyMidnightUTC(startStr),
    end: endStr ? turkeyMidnightUTC(endStr) : new Date().toISOString(),
  };
}

/** Human-readable label for a preset */
export function presetLabel(preset: DateRangePreset): string {
  const labels: Record<DateRangePreset, string> = {
    today: 'Bugün',
    yesterday: 'Dün',
    this_week: 'Bu Hafta',
    this_month: 'Bu Ay',
    this_year: 'Bu Yıl',
    last_30_days: 'Son 30 Gün',
  };
  return labels[preset];
}

/* ── Revenue summary ── */

export interface PaymentMethodStats {
  revenue: number;
  orders: number;
}

export interface RevenueSummary {
  totalRevenue: number;
  totalOrders: number;
  averageOrderValue: number;
  activeCustomers: number;
  pendingOrders: number;
  cancelledOrders: number;
  cancelledAmount: number;
  paymentBreakdown: {
    cash: PaymentMethodStats;
    credit_card: PaymentMethodStats;
    online_card: PaymentMethodStats;
    meal_card: PaymentMethodStats;
    yemeksepeti: PaymentMethodStats;
  };
}

const DELIVERED_STATUSES = new Set(['delivered', 'completed', 'fulfilled']);
const PENDING_STATUSES = new Set(['pending', 'confirmed', 'preparing', 'on_the_way']);

const toDateKey = (iso: string) => iso.slice(0, 10);

function normalizeRestaurantName(row: any): string {
  return row?.restaurants?.name ?? row?.restaurant_name ?? 'Unknown Restaurant';
}

export async function fetchRevenueSummary(
  preset: DateRangePreset,
  restaurantId?: string,
): Promise<RevenueSummary> {
  const { start, end } = getDateRange(preset);

  let query = supabase
    .from('orders')
    .select('id, total_amount, status, created_at, user_id, payment_method')
    .gte('created_at', start);
  if (restaurantId) query = query.eq('restaurant_id', restaurantId);
  if (preset === 'yesterday') query = query.lt('created_at', end);

  const { data, error } = await query;

  if (error) throw error;

  let totalRevenue = 0;
  let totalOrders = 0;
  let pendingOrders = 0;
  let cancelledOrders = 0;
  let cancelledAmount = 0;
  const customerSet = new Set<string>();

  const paymentBreakdown = {
    cash: { revenue: 0, orders: 0 },
    credit_card: { revenue: 0, orders: 0 },
    online_card: { revenue: 0, orders: 0 },
    meal_card: { revenue: 0, orders: 0 },
    yemeksepeti: { revenue: 0, orders: 0 },
  };

  (data ?? []).forEach((order: any) => {
    const status = (order.status ?? '').toLowerCase();
    const amount = Number(order.total_amount ?? 0);
    const method: string = (order.payment_method ?? '').toLowerCase();

    if (DELIVERED_STATUSES.has(status)) {
      totalRevenue += amount;
      totalOrders += 1;

      if (
        method === 'cash' ||
        method === 'credit_card' ||
        method === 'online_card' ||
        method === 'meal_card' ||
        method === 'yemeksepeti'
      ) {
        paymentBreakdown[method].revenue += amount;
        paymentBreakdown[method].orders += 1;
      }
    } else if (PENDING_STATUSES.has(status)) {
      pendingOrders += 1;
    } else if (status === 'cancelled') {
      cancelledOrders += 1;
      cancelledAmount += amount;
    }

    if (order.user_id) customerSet.add(order.user_id);
  });

  return {
    totalRevenue,
    totalOrders,
    averageOrderValue: totalOrders > 0 ? totalRevenue / totalOrders : 0,
    activeCustomers: customerSet.size,
    pendingOrders,
    cancelledOrders,
    cancelledAmount,
    paymentBreakdown,
  };
}

/* ── Keep old name as alias for any other callers ── */
export async function fetchExecutiveSummary(restaurantId?: string) {
  const summary = await fetchRevenueSummary('last_30_days', restaurantId);
  const todayRange = getDateRange('today');
  let query = supabase
    .from('orders')
    .select('total_amount, status')
    .gte('created_at', todayRange.start);
  if (restaurantId) query = query.eq('restaurant_id', restaurantId);

  // Compute today revenue separately for backward compat
  const { data } = await query;
  let todayRevenue = 0;
  (data ?? []).forEach((o: any) => {
    if (DELIVERED_STATUSES.has((o.status ?? '').toLowerCase())) {
      todayRevenue += Number(o.total_amount ?? 0);
    }
  });
  return { ...summary, todayRevenue };
}

/* ── Daily income chart data ── */

export interface DailyIncomePoint {
  date: string;
  restaurantId: string | null;
  restaurantName: string;
  totalRevenue: number;
  orderCount: number;
}

export async function fetchDailyIncome(rangeDays = 14, restaurantId?: string): Promise<DailyIncomePoint[]> {
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - (rangeDays - 1));
  start.setUTCHours(0, 0, 0, 0);

  let query = supabase
    .from('orders')
    .select('id, total_amount, status, created_at, restaurant_id, restaurants ( id, name )')
    .gte('created_at', start.toISOString())
    .order('created_at', { ascending: true });
  if (restaurantId) query = query.eq('restaurant_id', restaurantId);

  const { data, error } = await query;

  if (error) throw error;

  const aggregation = new Map<string, DailyIncomePoint>();

  (data ?? []).forEach((order: any) => {
    const status = (order.status ?? '').toLowerCase();
    if (!DELIVERED_STATUSES.has(status)) return;

    const dateKey = toDateKey(order.created_at);
    const restaurantId = order.restaurant_id ?? order.restaurants?.id ?? null;
    const key = `${dateKey}::${restaurantId ?? 'global'}`;
    const existing = aggregation.get(key) ?? {
      date: dateKey,
      restaurantId,
      restaurantName: normalizeRestaurantName(order),
      totalRevenue: 0,
      orderCount: 0,
    };

    existing.totalRevenue += Number(order.total_amount ?? 0);
    existing.orderCount += 1;
    aggregation.set(key, existing);
  });

  return Array.from(aggregation.values()).sort((a, b) => a.date.localeCompare(b.date));
}

/* ── Top sellers ── */

export interface TopSellerRecord {
  productId: string;
  productName: string;
  category: string | null;
  quantity: number;
  revenue: number;
}

export async function fetchTopSellers(rangeDays = 30, limit = 10): Promise<TopSellerRecord[]> {
  const start = new Date();
  start.setUTCDate(start.getUTCDate() - (rangeDays - 1));
  start.setUTCHours(0, 0, 0, 0);

  const { data, error } = await supabase
    .from('order_items')
    .select(
      `
        product_id,
        quantity,
        total_price,
        products ( id, name, category ),
        orders:orders (
          created_at,
          status
        )
      `
    )
    .gte('orders.created_at', start.toISOString());

  if (error) throw error;

  const byProduct = new Map<string, TopSellerRecord>();

  (data ?? []).forEach((row: any) => {
    const status = (row.orders?.status ?? '').toLowerCase();
    if (!DELIVERED_STATUSES.has(status)) return;

    const productId = row.product_id ?? row.products?.id;
    if (!productId) return;

    const existing = byProduct.get(productId) ?? {
      productId,
      productName: row.products?.name ?? 'Unnamed product',
      category: row.products?.category ?? null,
      quantity: 0,
      revenue: 0,
    };

    existing.quantity += Number(row.quantity ?? 0);
    existing.revenue += Number(row.total_price ?? 0);
    byProduct.set(productId, existing);
  });

  return Array.from(byProduct.values())
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, limit);
}

/* ── Adaptive revenue timeline ── */

export interface TimelinePoint {
  label: string;
  revenue: number;
  orderCount: number;
}

const TR_WEEKDAYS = ['Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt', 'Paz'];
const TR_MONTHS = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];

function turkeyDate(iso: string) {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Istanbul',
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', hour12: false,
  }).formatToParts(d);
  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '0';
  return {
    year: Number(get('year')),
    month: Number(get('month')),  // 1-12
    day: Number(get('day')),
    hour: Number(get('hour')),
    dow: new Date(`${get('year')}-${get('month')}-${get('day')}T12:00:00`).getDay(), // 0=Sun
  };
}

function buildBuckets(preset: DateRangePreset): TimelinePoint[] {
  const today = turkeyToday();
  const [y, m] = today.split('-').map(Number);

  switch (preset) {
    case 'today':
    case 'yesterday':
      return Array.from({ length: 24 }, (_, i) => ({
        label: `${String(i).padStart(2, '0')}:00`,
        revenue: 0,
        orderCount: 0,
      }));
    case 'this_week':
      return TR_WEEKDAYS.map((d) => ({ label: d, revenue: 0, orderCount: 0 }));
    case 'this_month': {
      const daysInMonth = new Date(y, m, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, i) => ({
        label: String(i + 1),
        revenue: 0,
        orderCount: 0,
      }));
    }
    case 'this_year':
      return TR_MONTHS.map((m) => ({ label: m, revenue: 0, orderCount: 0 }));
    case 'last_30_days': {
      const td = new Date(y, m - 1, Number(today.split('-')[2]));
      return Array.from({ length: 30 }, (_, i) => {
        const d = new Date(td);
        d.setDate(d.getDate() - 29 + i);
        return {
          label: `${d.getDate()}/${String(d.getMonth() + 1).padStart(2, '0')}`,
          revenue: 0,
          orderCount: 0,
        };
      });
    }
  }
}

function bucketIndex(preset: DateRangePreset, td: ReturnType<typeof turkeyDate>): number {
  switch (preset) {
    case 'today':
    case 'yesterday':
      return td.hour;
    case 'this_week': {
      const dow = td.dow; // 0=Sun
      return dow === 0 ? 6 : dow - 1; // Mon=0 … Sun=6
    }
    case 'this_month':
      return td.day - 1;
    case 'this_year':
      return td.month - 1;
    case 'last_30_days': {
      const today = turkeyToday();
      const [y2, m2, d2] = today.split('-').map(Number);
      const todayMs = new Date(y2, m2 - 1, d2).getTime();
      const orderMs = new Date(td.year, td.month - 1, td.day).getTime();
      const diff = Math.round((todayMs - orderMs) / 86_400_000);
      return 29 - diff;
    }
  }
}

export async function fetchRevenueTimeline(
  preset: DateRangePreset,
  restaurantId?: string,
): Promise<TimelinePoint[]> {
  const { start, end } = getDateRange(preset);

  let query = supabase
    .from('orders')
    .select('total_amount, status, created_at')
    .gte('created_at', start);
  if (restaurantId) query = query.eq('restaurant_id', restaurantId);
  if (preset === 'yesterday') query = query.lt('created_at', end);

  const { data, error } = await query;
  if (error) throw error;

  const buckets = buildBuckets(preset);

  (data ?? []).forEach((order: any) => {
    const status = (order.status ?? '').toLowerCase();
    if (!DELIVERED_STATUSES.has(status)) return;

    const td = turkeyDate(order.created_at);
    const idx = bucketIndex(preset, td);
    if (idx >= 0 && idx < buckets.length) {
      buckets[idx].revenue += Number(order.total_amount ?? 0);
      buckets[idx].orderCount += 1;
    }
  });

  return buckets;
}
